import type { AxiosError } from 'axios'
import { useDateFormat } from '@vueuse/core'
import type { OrderItem, WishlistItem } from './types'

// The store API answers 400 with 'addresses.limit_reached' and 422 with
// 'addresses.invalid_details'. Anything else is the validation pipe's prose array or axios's
// own "Request failed with status code 400", neither of which a customer should be shown.
// getErrorMessage below is the raw-prose counterpart and would surface exactly that string.
export const addressErrorKey = (error: unknown, fallbackKey: string): string => {
  const apiMessage = (error as AxiosError<{ message?: string | string[] }>)?.response?.data?.message

  return typeof apiMessage === 'string' && apiMessage.startsWith('addresses.') ? apiMessage : fallbackKey
}

export const getErrorMessage = (error: any, customMessage?: string) => {
  if (Array.isArray(error.response?.data?.message)) {
    return error.response.data.message.join(', ')
  }
  return (
    error?.response?.msg ||
    error?.data?.message ||
    error?.data?.msg ||
    error?.response?.data?.message ||
    error?.response?.data?.msg ||
    error?.response?._data?.message ||
    error?.response?._data?.msg ||
    error?.message ||
    customMessage ||
    'Something went wrong'
  )
}

export const formatDate = (date?: string) => {
  if (date) return useDateFormat(date, 'Do MMM, YYYY')
}
export const calculateAmount = (item: OrderItem) => {
  let amount = item.amount
  if (item.trialPeriod) {
    amount = 0
  }
  return amount + (item.setupFee ?? 0)
}
export const getProfitPercent = (item: WishlistItem) => {
  return `${(((item.compareAtPrice - item.amount) * 100) / item.compareAtPrice).toFixed(0)}% off`
}

// Resolved rather than prefix-checked: the URL parser turns `/\host` and embedded
// tabs or newlines into protocol-relative references, which a startsWith test lets
// through. Query and hash are kept - they carry the page state the storefront needs
// back, and an absolute same-origin value is reduced rather than rejected so either
// app can deploy first.
export const sameOriginPath = (target: string | null | undefined, origin: string): string => {
  if (!target) return '/'
  try {
    const url = new URL(target, origin)
    if (url.origin !== origin) return '/'
    // Checking the input is not enough: a same-origin absolute URL whose path begins
    // `//` yields a relative value that is protocol-relative on the way back out, so
    // the value we are about to return is resolved again and checked.
    const relative = url.pathname + url.search + url.hash
    return new URL(relative, origin).origin === origin ? relative : '/'
  } catch {
    return '/'
  }
}

// Both conditions are required. close() on a nested browsing context is a no-op anyway,
// but the branch must not be reachable from a frame: this login is a public URL that any
// page can frame, and only a top-level tab was opened to be closed.
export const shouldCloseOnSuccess = (search: string, isFramed: boolean): boolean =>
  !isFramed && new URLSearchParams(search).get('closeOnSuccess') === '1'
